import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { IonButton, IonText } from "@ionic/react";
import SignatureCanvas from "react-signature-canvas";
import "./SignaturePad.css";

export interface SignaturePadHandle {
    clear(): void;
}

interface SignaturePadProps {
    header?: React.ReactNode;
    wrapperRef?: React.RefObject<HTMLDivElement>;
    onDone: (dataUrl: string) => void;
    beforeDone?: () => boolean;
}

const SignaturePad = forwardRef<SignaturePadHandle, SignaturePadProps>(
    ({ header, wrapperRef, onDone, beforeDone }, ref) => {
        const sigRef = useRef<SignatureCanvas>(null);
        const [hasSignature, setHasSignature] = useState(false);
        const [error, setError] = useState<string | null>(null);

        useImperativeHandle(ref, () => ({
            clear() {
                sigRef.current?.clear();
                setHasSignature(false);
                setError(null);
            },
        }));

        const updateHasSignature = () => {
            const empty = sigRef.current?.isEmpty() ?? true;
            setHasSignature(!empty);
            if (!empty) setError(null);
        };

        const handleClear = () => {
            sigRef.current?.clear();
            setHasSignature(false);
            setError(null);
        };

        const handleDone = () => {
            if (beforeDone && !beforeDone()) return;

            const pad = sigRef.current;
            if (!pad || pad.isEmpty()) {
                setError("Signature is required.");
                setHasSignature(false);
                return;
            }

            const dataUrl = pad.toDataURL("image/png");
            handleClear();
            onDone(dataUrl);
        };

        return (
            <>
                <div ref={wrapperRef} className="sign-pad-wrapper">
                    {header && <div className="sign-pad-header">{header}</div>}
                    <SignatureCanvas
                        ref={sigRef}
                        penColor="#111"
                        minWidth={1}
                        maxWidth={2.5}
                        onBegin={() => setError(null)}
                        onEnd={updateHasSignature}
                        canvasProps={{
                            className: "sign-pad",
                            width: 520,
                            height: 220,
                            style: {
                                touchAction: "none",
                            },
                        }}
                    />
                </div>

                {error && (
                    <IonText color="danger">
                        <p style={{ marginTop: 8 }}>{error}</p>
                    </IonText>
                )}

                <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                    <IonButton expand="block" fill="outline" onClick={handleClear}>
                        Clear
                    </IonButton>
                    <IonButton expand="block" onClick={handleDone} disabled={!hasSignature}>
                        Done
                    </IonButton>
                </div>
            </>
        );
    }
);

export default SignaturePad;
